<template>
  <div class="conversation" v-if="!isHidingConversation">
    <v-overlay
      attach=".chat-text-input"
      class="confirm-disconnect-dialog"
      v-model="isShowConfirmDialog"
    >
      <v-card class="overlay-dialog">
        <v-card-title class="dialog-title">
          <v-img
            class="dialog-icon"
            cover
            :src="widgetData.config.disconnectChatDialogIconLink"
          ></v-img>
        </v-card-title>
        <v-card-text class="dialog-text">
          {{ widgetData.config.disconnectChatDialogText }}
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn
            class="close-dialog-button"
            @click="onConfirmDisconnectChatDialog()"
          >
            {{ conversationView.okBtnLabel ? conversationView.okBtnLabel : 'OK'}}
          </v-btn>
          <v-btn
            class="close-dialog-button"
            @click="isShowConfirmDialog = false"
          >
          {{ conversationView.cancelBtnLabel ? conversationView.cancelBtnLabel : 'Cancel'}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-overlay>

    <!-- start testing header design  -->
    <div class="header" v-show="$store.state.currentView.viewName === 'chat'">
      <v-card class="mx-auto header-content">
        <v-card-text>
          <img class="title-icon-img" :src="conversationView.titleIconLink" />
          <span class="participant-name">{{ participantName }}</span>

          <!-- disconnect button -->
          <v-tooltip
            location="bottom"
            :text="conversationView.disconnectBtnLabel"
            v-if="isConnect"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="restart-button"
                id="disconnect-btn"
                size="x-small"
                @click="
                  onDisconnectButtonClick();
                  showTooltip('tooltip-disconnect-btn');
                "
                icon
              >
                <img
                  :src="conversationView.disconnectBtnImageLink"
                  style="width: 35px"
                />
              </v-btn>
            </template>
          </v-tooltip>
          <!-- end disconnect button -->

          <!-- translate section -->
          <div
            v-if="
              translationConfig &&
              translationConfig.activationState &&
              (translationConfig.activationState.toUpperCase() == 'READONLY' ||
                translationConfig.activationState.toUpperCase() == 'ENABLED')
            "
          >
            <v-tooltip location="bottom" :text="getTranslateButtonText()">
              <template v-slot:activator="{ props }">
                <div>
                  <v-switch
                    v-bind="props"
                    class="translate-switch-wrapper"
                    v-model="innerIsTranslationOn"
                    color="info"
                    :disabled="
                      translationConfig &&
                      translationConfig.activationState &&
                      translationConfig.activationState.toUpperCase() ==
                        'READONLY'
                    "
                    @update:modelValue="onTranslateSwitched"
                  >
                  </v-switch>
                </div>
              </template>
            </v-tooltip>
            <img
              style="position: absolute; right: 95px; bottom: 20px"
              :src="translationConfig.translateImageLink"
            />
          </div>
          <!-- end translate section -->

          <!-- restart button -->
          <v-tooltip
            location="bottom"
            v-if="!isConnect"
            :text="conversationView.restartBtnLabel"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                v-if="!isConnect"
                class="restart-button"
                id="restart-btn"
                size="x-small"
                @click="
                  $EventBus.emit('handleRestartButtonClick');
                  showTooltip('tooltip-restart');
                "
                icon
              >
                <img
                  :src="conversationView.restartBtnImageLink"
                  style="width: 35px"
                />
              </v-btn>
            </template>
          </v-tooltip>
          <!-- end restart button -->

          <!-- switch to video button -->
          <template
            v-if="
              isConnect &&
              $store.state.videoMessage &&
              $store.state.videoMessage.text &&
              !cannotStartAgentVideo
            "
          >
            <v-tooltip location="bottom" :text="conversationView.videoBtnLabel">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  id="back-btn"
                  size="x-small"
                  @click="
                    handleChatVideoSwitch();
                    showTooltip('tooltip-video');
                  "
                  icon
                  class="video-button"
                >
                  <img
                    style="width: 35px"
                    :src="conversationView.videoBtnImageLink"
                  />
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <!-- end switch to video button -->
        </v-card-text>
      </v-card>
    </div>
    <!-- end testing header design  -->
    <div
      class="content"
      :class="{
        'chat-content':
          currentView === 'chat' && !isShowHoldVideo && !isShowWelcomeVideo,
      }"
      ref="conversationContent"
      :style="{ height: getContentHeight }"
    >
      <balloon
        v-for="c in conversationData"
        :data="c"
        :key="c.id"
        v-show="
          currentView === 'chat' && !isShowHoldVideo && !isShowWelcomeVideo
        "
      ></balloon>
      <div
        class="agent-typing-indicator"
        v-show="
          currentView === 'chat' &&
          !isShowHoldVideo &&
          !isShowWelcomeVideo &&
          agentTypingStatus == 1
        "
      >
        <v-img
          v-if="conversationView.progressIconLink"
          :src="conversationView.progressIconLink"
        ></v-img>
        <v-img
          v-else-if="!conversationView.progressIconLink"
          :src="require('@/assets/Progress.svg')"
        ></v-img>
      </div>
      <VideoView
        v-if="$store.state.videoMessage"
        v-show="
          currentView === 'video' && !isShowHoldVideo && !isShowWelcomeVideo
        "
      />
      <v-card
        v-if="isShowHoldVideo && !isShowWelcomeVideo"
        class="elevation-16 mx-auto video-card"
        width="100%"
        style="height: calc(100% - 13px)"
        color="transparent"
      >
        <video
          v-if="videoConfig && videoConfig.holdVideoUrl"
          id="holdVideo"
          controls
          muted="muted"
          autoplay
          loop
          style="height: 100%; width: 100%"
          playsinline
        >
          <source :src="videoConfig.holdVideoUrl" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <!-- in case there no any pre video content -->
        <v-card
          v-if="
            videoConfig &&
            (!videoConfig.holdVideoUrl || videoConfig.holdVideoUrl == '')
          "
          color="transparent"
          class="waiting-content"
        >
          <img class="waiting-image" :src="widgetData.config.prechatLogo" />
          <div class="waiting-title">{{ enterChatView.title }}</div>
          <div class="waiting-title">{{ videoConfig.onHoldMsg }}</div>
          <v-img
            v-if="conversationView.progressIconLink"
            class="waiting-title"
            :src="conversationView.progressIconLink"
          ></v-img>
          <v-img
            v-else-if="!conversationView.progressIconLink"
            class="waiting-title"
            :src="require('@/assets/Progress.svg')"
          ></v-img>
        </v-card>
      </v-card>
      <v-card
        v-if="isShowWelcomeVideo && !isShowHoldVideo"
        class="elevation-16 mx-auto video-card"
        width="100%"
        style="height: calc(100% - 13px)"
        color="transparent"
      >
        <video
          id="welcomeVideo"
          controls
          v-if="kioskView && kioskView.preVideoUrl"
          muted="muted"
          autoplay
          loop
          style="height: 100%; width: 100%"
          playsinline
        >
          <source :src="kioskView.preVideoUrl" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <!-- in case there no any pre video content -->
        <v-card
          v-if="
            kioskView && (!kioskView.preVideoUrl || kioskView.preVideoUrl == '')
          "
          color="transparent"
          class="waiting-content"
        >
          <img class="waiting-image" :src="widgetData.config.prechatLogo" />
          <div class="waiting-title">{{ enterChatView.title }}</div>
          <div class="waiting-title">{{ kioskView.waitingInQueueMsg }}</div>
          <v-img
            v-if="conversationView.progressIconLink"
            class="waiting-title"
            :src="conversationView.progressIconLink"
          ></v-img>
          <v-img
            v-else-if="!conversationView.progressIconLink"
            class="waiting-title"
            :src="require('@/assets/Progress.svg')"
          ></v-img>
        </v-card>
      </v-card>
      <v-snackbar
        location="top"
        :color="snackbarColor"
        v-model="isShowSnackbarVideo"
        :timeout="200000000"
        style="margin-top: 140px"
        z-index="1000000000000000000000000"
        >{{ snackbarText }}</v-snackbar
      >
      <v-snackbar
        :color="snackbarColor"
        v-model="isShowSnackbar"
        :timeout="2000"
      >
        {{ snackbarText }}
        <template v-slot:actions>
          <v-btn text @click="isShowSnackbar = false" color="white">X</v-btn>
        </template>
      </v-snackbar>
    </div>

    <div
      class="input"
      v-if="
        isConnect && !onHold && $store.state.currentView.viewName === 'chat'
      "
    >
      <v-textarea
        :loading="working"
        variant="solo"
        v-model.trim="chatInputText"
        :label="chatInputText ? '' : conversationView.chatInputLabel"
        @keyup.enter="onSendMessage()"
        @keydown="onKeydown()"
        autocomplete="off"
        id="chat-text-input"
        class="chat-text-input"
        :color="widgetData.config.defaultThemeColor"
        rows="1"
        auto-grow
      >
        <template
          v-slot:prepend-inner
          v-if="widgetData.config.showUploadIcon && canUpload"
        >
          <img
            class="upload-file-button"
            @click="onUploadFileClicked"
            style="cursor: pointer"
            :src="
              conversationView.attachFileIconLink ||
              require('@/assets/Attach_file.svg')
            "
            alt="Attach File"
          />
        </template>

        <template
          v-if="conversationView.showSendBtn && !!chatInputText"
          v-slot:append-inner
        >
          <img
            class="send-message-button"
            style="cursor: pointer"
            @click="onSendMessage"
            :src="conversationView.sendIconLink || require('@/assets/Send.svg')"
            alt="Send Icon"
          />
        </template>
      </v-textarea>

      <input
        ref="fileUploadInput"
        style="display: none"
        type="file"
        @change="onFilePicked"
      />
    </div>
  </div>
</template>

<script>
import Utils from "../helpers/utils";
import Balloon from "../components/Balloon";
import VideoView from "./VideoView";
import { mapGetters } from "vuex";
import VidyoService from "../services/VidyoService";
import ErrorService from "../services/ErrorService";
import { watch } from "vue";

export default {
  components: { Balloon, VideoView },
  name: "ConversationView",
  created() {
    Utils.log.debug("ConversationView created, isConnect=" + this.isConnect);
    this.$EventBus.on("disconnectChatTriggred", () => {
      this.onDisconnectButtonClick();
    });
  },
  data() {
    return {
      isShowSnackbar: false,
      isShowSnackbarVideo: false,
      snackbarColor: "success",
      onkeyupTimer: null,
      // canCreateVideo: false,
      // chatEventHeader: "[ccspChatEvent]",
      data: {
        currentCallID: undefined,
      },
      chatInputText: "",
      working: false,
      snackbarText: "",
      canUpload: true,
      onHold: false,
      cannotStartAgentVideo: false,
      isShowConfirmDialog: false,
      isSrcLangDetected: true,
      innerIsTranslationOn: false,
    };
  },
  computed: {
    ...mapGetters([
      "conversationData",
      "chatData",
      "appData",
      "widgetData",
      "agentTypingStatus",
      "participantName",
      "isConnect",
      "isAgentRespond",
      "enterChatView",
      "conversationView",
      "videoView",
      "videoConfig",
      "currentView",
      "kioskView",
      "callOnHold",
      "agentAutoMessage",
      "agentArrivedToVideo",
      "afterChatView",
      "msgTrDelimeter",
      "translationConfig",
      "isTranslationOn",
    ]),
    isMobile() {
      return Utils.mobileCheck();
    },
    isShowTooltipOnHover() {
      return (
        (this.isMobile && !this.widgetData.config.disableTooltipOnMobile) ||
        !this.isMobile
      );
    },
    getContentHeight() {
      return this.$store.state.currentView.viewName === "video"
        ? "100%"
        : "calc(100% - 200px)";
    },
    isShowHoldVideo() {
      return (
        this.widgetData.config.isKioskMode &&
        this.callOnHold &&
        !this.cannotStartAgentVideo
      );
    },
    isShowWelcomeVideo() {
      return (
        this.widgetData.config.isKioskMode &&
        this.widgetData.config.isKioskNoChatUntilVideo &&
        !this.agentArrivedToVideo &&
        !this.cannotStartAgentVideo
      );
    },
    isHidingConversation() {
      return this.$store.state.hideConversation;
    },
  },
  mounted() {
    Utils.log.debug(
      `ConversationView mounted, isConnect=${
        this.isConnect
      } resumed=${sessionStorage.getItem("cc_resumed")} isHidingConversation=${
        this.isHidingConversation
      }`
    );
    if (sessionStorage.getItem("cc_resumed") && !this.isConnect) {
      Utils.log.debug(
        "ConversationView was from a resumed call but is now not connected"
      );
      sessionStorage.removeItem("cc_resumed");
    }

    if (!sessionStorage.getItem("cc_resumed")) {
      this.$store.commit(
        "setIsTranslationOn",
        this.translationConfig &&
          (this.translationConfig.activationState == "HIDDEN" ||
            this.translationConfig.isDefaultModeOn)
      );
    }
    this.innerIsTranslationOn = this.$store.state.isTranslationOn;

    watch(this.agentAutoMessage, (newVal, oldVal) => {
      Utils.log.debug("agentAutoMessage watcher, message is: " + newVal);
      if (newVal === "can_not_start_video") {
        // this.$store.commit("setCurrentView", "chat");
        this.cannotStartAgentVideo = true;
      }
    });
    watch(this.conversationData, (newVal, oldVal) => {
      Utils.log.debug(
        "conversationData watcher, conversationData is: " + newVal
      );
      // Scroll to the bottom of conversation,
      // when conversation array changed
      // and after it appears on DOM (therefore the "settimeout").
      setTimeout(() => {
        if (this.$refs.conversationContent) {
          this.$refs.conversationContent.scrollTop = 100000000000; //very down
        }
      }, 100);
    });
    watch(this.callOnHold, (newVal, oldVal) => {
      Utils.log.debug(
        "conversationView watcher - callOnHold, newVal is: ",
        newVal
      );
      this.onHold = newVal;

      let vid = document.getElementById("holdVideo");
      if (newVal === true) {
        if (
          this.videoConfig &&
          this.videoConfig.onHoldMsg &&
          this.videoConfig.holdVideoUrl
        ) {
          this.showSnackbarVideo(this.videoConfig.onHoldMsg, "purple");
        }
        if (vid) {
          vid.play();
        }
      } else {
        this.isShowSnackbarVideo = false;
        if (vid) {
          vid.muted = true;
          vid.pause();
          vid.currentTime = 0;
        }
      }
    });
    watch(this.agentArrivedToVideo, (newVal, oldVal) => {
      if (newVal) {
        let vid = document.getElementById("welcomeVideo");

        if (vid) {
          vid.play();
        }
      }
    });
  },
  methods: {
    getTranslateButtonText() {
      try {
        var before = this.translationConfig.translateSwitchLabel;
        var s = this.$store.state.translationSourceLang
          ? this.$store.state.translationSourceLang
          : "unknown";
        var t = this.translationConfig.targetLanguage;
        return `${before}${s} => ${t}`;
      } catch (err) {
        Utils.log.debug("getTranslateButtonText error", err);
        return "unknown";
      }
    },
    onTranslateSwitched(val) {
      //  if(this.isTranslationOn){
      //     let message = this.translationConfig.translationOnLabel || "Translate Is On"
      //     ErrorService.displayAlert(message, "info");
      //   }
      //   else{
      //       let message = this.translationConfig.translationOffLabel ||"Translate Is Off"
      //       ErrorService.displayAlert(message, "info");
      //   }
      this.$store.commit("setIsTranslationOn", val);
      if (
        this.translationConfig &&
        this.translationConfig.detectionType &&
        this.translationConfig.detectionType.toUpperCase() == "AUTO"
      ) {
        this.$store.commit("setTranslationSourceLang", null);
      }
      this.showTooltip("tooltip-translation-switch");
      return val;
    },
    showTooltip(id) {
      if (
        (!Utils.mobileCheck() && !Utils.isSafari()) ||
        (Utils.mobileCheck() && this.widgetData.config.disableTooltipOnMobile)
      ) {
        return;
      }
      let el = document.querySelector("#" + id);
      if (el) {
        Utils.log.debug("showTooltip: " + id);
        document.querySelector("#" + id).parentElement.style.display = "block";
        document.querySelector("#" + id).parentElement.style.opacity = "0.9";
        setTimeout(() => {
          document.querySelector("#" + id).parentElement.style.display = "none";
        }, 1000);
      }
    },
    handleChatVideoSwitch() {
      if (this.$store.state.currentView.viewName === "video") {
        this.$store.commit("setCurrentView", "chat");
      } else {
        this.$store.commit("setCurrentView", "video");
      }
    },
    onUploadFileClicked() {
      this.$refs.fileUploadInput.click();
    },

    onDisconnectButtonClick() {
      if (
        this.widgetData.config.isShowDisconnectChatDialog &&
        !this.isShowConfirmDialog
      ) {
        this.isShowConfirmDialog = true;
        return;
      }

      Utils.postMessageObject("status", "Contact disconnected by consumer");
      this.$store.dispatch("leaveChat", { isAsync: true });
      this.$store.commit("setConnectedToCallCenter", false);
      VidyoService.disconnectVidyoCall();

      // if (this.widgetData.config.isKioskMode) return;
      if (this.widgetData.config.isPostSurvey && this.isAgentRespond) {
        this.$router.push("PostSurveyView");
      } else {
        this.$router.push("AfterView");
      }
    },

    onConfirmDisconnectChatDialog() {
      this.onDisconnectButtonClick();
      this.isShowConfirmDialog = false;
    },

    async setTypingStatus(typing_status) {
      await this.$store.dispatch("setTypingStatus", typing_status);
    },

    async getTranslationResult(text) {
      if (
        this.translationConfig &&
        this.translationConfig.detectionType &&
        this.translationConfig.detectionType.toUpperCase() == "AUTO" &&
        !this.$store.state.translationSourceLang
      ) {
        let dataObj = {
          url: `${
            this.translationConfig.translationURL
          }/getLanguage?text=${encodeURIComponent(text)}`,
        };
        try {
          let detectedSrcLang = await this.$store.dispatch(
            "getTextLanguage",
            dataObj
          );
          if (detectedSrcLang && detectedSrcLang !== "") {
            this.$store.commit("setTranslationSourceLang", detectedSrcLang[0]);
            let translatedTxt = await this.getTranslatedText(text);
            return translatedTxt;
          } else {
            return `${text}`;
          }
        } catch (e) {
          return `${text}`;
        }
      } else {
        let translatedTxt = await this.getTranslatedText(text);
        return translatedTxt;
      }
    },

    async getTranslatedText(text) {
      try {
        //check if need to translate
        if (
          !this.$store.state.translationSourceLang ||
          (this.$store.state.translationSourceLang &&
            this.translationConfig.targetLanguage.toUpperCase() ==
              this.$store.state.translationSourceLang.toUpperCase())
        ) {
          Utils.log.debug(
            `SOURCE LANG: ${this.$store.state.translationSourceLang} . TARGET LANG: ${this.translationConfig.targetLanguage}. return the original text`
          );
          return `${text}`;
        }
        //do translate
        let url = `${
          this.translationConfig.translationURL
        }/getTranslation?text=${encodeURIComponent(text)}&destLanguage=${
          this.translationConfig.targetLanguage
        }`;
        if (this.$store.state.translationSourceLang) {
          url += `&srcLanguage=${this.$store.state.translationSourceLang}`;
        }
        let dataObj = { url: url };
        if (this.translationConfig.isShowAlerts) {
          let msg = this.translationConfig.translatingLabel || "Translating..";
          ErrorService.displayAlert(msg, "info", 1000);
        }

        let data = await this.$store.dispatch("getTranslatedText", dataObj);
        if (data) {
          return `${data}`;
        } else {
          return `${text}`;
        }
      } catch (err) {
        Utils.log.debug("getTranslatedText error", err);
        return `${text}`;
      }
    },

    async sendMessage(strText) {
      try {
        Utils.log.debug("sendMessage:" + strText);
        this.$store.commit("setDataBeforeSendMessage", strText);
        await this.$store.dispatch("sendMessage");
        this.working = false;
        this.chatInputText = "";
      } catch (err) {
        Utils.log.debug("sendMessage error", err);
        this.working = false;
      }
    },

    async onFilePicked(e) {
      let fd = new FormData();
      Utils.log.debug(e.currentTarget.files[0], "(e.currentTarget.files[0],)");
      fd.append("file", e.currentTarget.files[0]);
      let fileName = e.currentTarget.files[0].name;
      this.showSnackbar(this.conversationView.uploadFileText, "info");
      this.canUpload = false;
      let messageText = this.getNewMessageParams(
        `Uploading file: "${fileName}" ...`
      );
      this.addChatLine(Object.assign({}, messageText));
      let data = await this.$store.dispatch("uploadFile", fd);
      if (data.isSuccedeed) {
        this.showSnackbar(
          this.conversationView.uploadFileSuccessText,
          "success"
        );
        messageText = this.getNewMessageParams(
          `Upload file: "${fileName}" succeeded`
        );
        this.addChatLine(Object.assign({}, messageText));
        this.sendMessage(data.filePath);
      } else {
        messageText = this.getNewMessageParams(
          `Upload file: "${fileName}" Failed`
        );
        this.addChatLine(Object.assign({}, messageText));
        this.showSnackbar(this.conversationView.uploadFileFailedText, "error");
        Utils.log.error(data.error);
      }
      this.canUpload = true;
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.isShowSnackbar = true;
    },
    showSnackbarVideo(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.isShowSnackbarVideo = true;
    },

    onKeydown() {
      //Set typeing status only if not exist
      if (this.onkeyupTimer != null) {
        window.clearTimeout(this.onkeyupTimer);
        this.onkeyupTimer = null;
      } else {
        this.setTypingStatus(1);
      }
      //release typeing status after 5000 ms
      this.onkeyupTimer = window.setTimeout(() => {
        this.setTypingStatus(2);
        this.onkeyupTimer = null;
      }, 5000);
    },

    async onSendMessage() {
      if (this.chatInputText === "") {
        return;
      }
      if (this.working) return;
      this.working = true;

      let translatedTxt = "";
      let fullTranslatedText = "";
      try {
        //only call translate when conditions fullfilled
        if (
          this.translationConfig &&
          this.translationConfig.activationState &&
          (this.translationConfig.activationState.toUpperCase() == "HIDDEN" ||
            this.translationConfig.activationState.toUpperCase() ==
              "READONLY" ||
            this.translationConfig.activationState.toUpperCase() ==
              "ENABLED") &&
          this.isTranslationOn
        ) {
          translatedTxt = await this.getTranslationResult(this.chatInputText);
          fullTranslatedText = `${this.chatInputText}${this.msgTrDelimeter}${translatedTxt}${this.msgTrDelimeter}${this.$store.state.translationSourceLang}${this.msgTrDelimeter}${this.translationConfig.targetLanguage}`;
        }

        // Assigning a temporary ID to the chat:
        // Using our addChatLine method to add the chat
        // to the screen immediately, without waiting for
        // the AJAX request to complete:

        this.addChatLine(
          Object.assign(
            {},
            this.getNewMessageParams(this.chatInputText, translatedTxt, {
              src: this.$store.state.translationSourceLang,
              trg: this.translationConfig
                ? this.translationConfig.targetLanguage
                : null,
            })
          )
        );

        if (
          translatedTxt &&
          translatedTxt != "" &&
          translatedTxt !== this.chatInputText
        ) {
          this.sendMessage(fullTranslatedText);
          return;
        }
        this.sendMessage(this.chatInputText);
      } catch (err) {
        Utils.log.debug("onSendMessage error", err);
        this.working = false;
      }
    },

    getNewMessageParams(text, translatedText = "", translatedData = null) {
      let tempID = "t" + Math.round(Math.random() * 1000000);
      return {
        id: tempID,
        author: this.chatData.name,
        author_type: "me",
        gravatar: this.chatData.gravatar,
        text: text.replace(/</g, "&lt;").replace(/>/g, "&gt;"),
        translatedText: translatedText,
        translatedData: translatedData,
      };
    },

    addChatLine(params) {
      this.$store.commit("addChatLine", params);
    },
  },
};
</script>

<style scoped>
.conversation {
  width: 100%;
  height: 100%;
  background-color: #f5f8fc;
}
.conversation > .header {
  height: 65px;
}

.conversation > .header > .header-content {
  box-shadow: 0px 12px 20px 0px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  border-radius: 0;
}

.header-content .participant-name {
  font-size: 11pt;
  color: rgba(0, 0, 0, 0.52);
}

.conversation > .content {
  overflow-y: auto;
}
.conversation > .input {
  height: 80px;
}

.conversation > .content.chat-content,
.conversation > .input {
  padding: 0 15px;
}

.conversation .agent-typing-indicator {
  background: #4fc1ec;
  padding: 20px;
  width: 80px;
  border-radius: 25px 25px 25px 0;
}

.title-icon-img {
  padding-right: 5px;
  max-width: 30px;
  display: inline-block;
  margin-right: 10px;
}

.chat-text-input:not(.v-input--is-focused)
  :v-deep(.v-input__slot:hover)
  fieldset,
.chat-text-input :v-deep(fieldset) {
  border-width: 0px;
  border-color: transparent;
  border-radius: 100px !important;
}

.overlay-dialog {
  width: 80%;
  margin: auto;
  background: #fff;
}

.overlay-dialog .dialog-title {
  justify-content: center;
  padding-top: 20px;
}

.overlay-dialog .dialog-icon {
  max-width: 50px;
}

.overlay-dialog .dialog-text {
  font-size: 15pt;
  color: #000;
  padding: 25px;
  text-align: center;
}

.overlay-dialog .dialog-actions {
  padding: 25px;
  display: flex;
  justify-content: center;
}

.translate-switch-wrapper {
  position: absolute;
  right: 50px;
  top: 0px;
}

.restart-button {
  right: 5px;
  top: 10px;
  position: absolute;
}

.video-button {
  right: 5px;
  top: 50px;
  position: fixed;
}

.send-messege-button {
  right: 5px;
  top: 10px;
  position: absolute;
  cursor: pointer;
}

.v-textarea {
  position: absolute;
  bottom: 0;
  width: calc(100% - 30px);
}

::v-deep .v-field.v-field--center-affix.v-field--prepended.v-field--variant-solo.v-theme--light.v-locale--is-ltr {
  border-radius: 30px;
}
</style>
